/**
 * API Client for AIVA Customer Dashboard
 * Handles all communication with the AIVA API
 */

import { getApiBaseUrl } from '@/utils/routing';

// Type definitions
export interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  avatar?: string;
  createdAt: string;
  totalOrders: number;
  totalSpent: number;
  lifetimeValue: number;
  rfmSegment?: string;
  pets?: Pet[];
}

export interface Pet {
  id: string;
  name: string;
  type: 'dog' | 'cat' | 'other';
  breed?: string;
  weight?: number;
  weightUnit?: 'kg' | 'lbs';
  age?: number;
  ageUnit?: 'months' | 'years';
  birthday?: string;
  avatar?: string;
  dietaryNeeds?: string[];
  allergies?: string[];
}

export interface Subscription {
  id: string;
  status: 'active' | 'paused' | 'cancelled';
  frequency: 'weekly' | 'biweekly' | 'monthly' | 'every-6-weeks';
  nextOrderDate: string;
  lastOrderDate?: string;
  pausedUntil?: string;
  cancelledAt?: string;
  items: SubscriptionItem[];
  totalPrice: number;
  price?: number; // alias for totalPrice used in some components
  currency: string;
  discount?: {
    code: string;
    amount: number;
    type: 'percentage' | 'fixed';
  };
}

export interface SubscriptionItem {
  id: string;
  productId: string;
  productName: string;
  variantId: string;
  variantName?: string;
  quantity: number;
  price: number;
  image?: string;
}

export interface Order {
  id: string;
  orderNumber: string;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  createdAt: string;
  totalPrice: number;
  currency: string;
  items: OrderItem[];
  shippingAddress?: Address;
  trackingNumber?: string;
  trackingUrl?: string;
  estimatedDelivery?: string;
}

export interface OrderItem {
  id: string;
  productName: string;
  variantName?: string;
  quantity: number;
  price: number;
  image?: string;
}

export interface Address {
  firstName: string;
  lastName: string;
  address1: string;
  address2?: string;
  city: string;
  province?: string;
  country: string;
  zip: string;
  phone?: string;
}

export interface AffiliateStats {
  referralCode: string;
  totalReferrals: number;
  activeReferrals: number;
  totalEarnings: number;
  pendingEarnings: number;
  currency: string;
  conversionRate: number;
  referralUrl: string;
}

// Loyalty Types
export interface LoyaltyOverview {
  enrolled: boolean;
  currentBalance: number;
  totalEarned: number;
  totalRedeemed: number;
  currentTier: string;
  tierSince?: string;
  pointsToNextTier?: number;
  referralCount: number;
  reviewsSubmitted: number;
  recentTransactions: LoyaltyTransaction[];
  program?: {
    name: string;
    pointsPerDollar: number;
    referralEnabled: boolean;
  };
}

export interface LoyaltyTransaction {
  type: string;
  points: number;
  reason: string;
  createdAt: string;
}

export interface TierProgress {
  enrolled: boolean;
  currentTier: string;
  currentMultiplier: number;
  currentBenefits: string[];
  totalPointsEarned: number;
  tierSince?: string;
  nextTier?: {
    name: string;
    minPoints: number;
    pointsNeeded: number;
    multiplier: number;
    benefits: string[];
  };
  allTiers: TierInfo[];
}

export interface TierInfo {
  name: string;
  minPoints: number;
  multiplier: number;
  benefits: string[];
  isCurrent: boolean;
  isUnlocked: boolean;
}

export interface Badge {
  id: string;
  name: string;
  description?: string;
  iconUrl?: string;
  iconEmoji?: string;
  category: string;
  rarity: string;
  bonusPoints?: number;
  earnedAt?: string;
  isDisplayed?: boolean;
  earned?: boolean;
}

export interface BadgesResponse {
  earned: Badge[];
  available: Badge[];
  totalEarned: number;
  totalAvailable: number;
}

export interface Challenge {
  id: string;
  name: string;
  description?: string;
  goalJson: string;
  pointsReward: number;
  badgeRewardId?: string;
  startDate: string;
  endDate: string;
  maxParticipants?: number;
}

export interface ChallengeWithProgress {
  challenge: Challenge;
  isJoined: boolean;
  progressPercent: number;
  isCompleted: boolean;
}

export interface ChallengesResponse {
  challenges: ChallengeWithProgress[];
  activeCount: number;
  joinedCount: number;
  completedCount: number;
}

export interface ReferralSummary {
  enrolled: boolean;
  referralCode?: string;
  discountCode?: string;
  totalReferrals: number;
  successfulReferrals?: number;
  totalReferredRevenue?: number;
  totalPointsEarned: number;
  firstOrderPoints?: number;
  milestonePoints?: number;
  recentReferrals?: ReferredCustomer[];
  milestones?: Milestone[];
}

export interface ReferredCustomer {
  anonymizedName: string;
  referredAt: string;
  hasPurchased: boolean;
  pointsEarned: number;
}

export interface Milestone {
  milestoneType: string;
  pointsAwarded: number;
  triggerAmount: number;
  achievedAt: string;
}

export interface EnrollReferralResult {
  success: boolean;
  referralCode?: string;
  discountCode?: string;
  dashboardUrl?: string;
  signupBonusPoints?: number;
  error?: string;
}

export interface ConvertPointsResult {
  success: boolean;
  pointsConverted: number;
  creditReceived: number;
  newPointsBalance: number;
  newCreditBalance: number;
}

export interface StoreCredit {
  currentBalance: number;
  totalEarned: number;
  totalUsed: number;
  pointsConverted: number;
  transactions: StoreCreditTransaction[];
}

export interface StoreCreditTransaction {
  type: string;
  amount: number;
  reason: string;
  createdAt: string;
}

export interface PrizeDraw {
  id: string;
  title: string;
  description: string;
  prizeValue: number;
  currency: string;
  drawDate: string;
  status: 'upcoming' | 'active' | 'drawn' | 'completed';
  entryCount: number;
  myEntries: number;
  image?: string;
  winner?: {
    name: string;
    date: string;
  };
}

export interface Coupon {
  code: string;
  description: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  expiresAt?: string;
  minPurchase?: number;
  usageCount: number;
  usageLimit?: number;
}

// API Client Class
class ApiClient {
  private baseUrl: string;
  private token: string | null = null;

  constructor() {
    this.baseUrl = getApiBaseUrl();
    this.token = this.getStoredToken();
    console.log('[ApiClient] Initialized with baseUrl:', this.baseUrl, 'token:', this.token ? 'present' : 'null');
  }

  // Token management
  private getStoredToken(): string | null {
    const token = localStorage.getItem('customer_token');
    console.log('[ApiClient] getStoredToken:', token ? 'found' : 'not found');
    return token;
  }

  public setToken(token: string): void {
    console.log('[ApiClient] setToken called:', token.substring(0, 10) + '...');
    this.token = token;
    localStorage.setItem('customer_token', token);
  }

  public clearToken(): void {
    this.token = null;
    localStorage.removeItem('customer_token');
  }

  public isAuthenticated(): boolean {
    const result = this.token !== null;
    console.log('[ApiClient] isAuthenticated:', result, 'token:', this.token ? 'present' : 'null');
    return result;
  }

  public getToken(): string | null {
    return this.token;
  }

  // HTTP request wrapper
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    };

    if (this.token) {
      headers['X-Customer-Token'] = this.token;
    }

    const config: RequestInit = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        if (response.status === 401) {
          this.clearToken();
          throw new Error('Authentication required');
        }
        throw new Error(`API Error: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API Request failed:', error);
      throw error;
    }
  }

  // Customer endpoints
  async getCustomer(): Promise<Customer> {
    return this.request<Customer>('/customer/profile');
  }

  async updateCustomer(data: Partial<Customer>): Promise<Customer> {
    return this.request<Customer>('/customer/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Pet endpoints
  async getPets(): Promise<Pet[]> {
    return this.request<Pet[]>('/customer/pets');
  }

  async createPet(data: Omit<Pet, 'id'>): Promise<Pet> {
    return this.request<Pet>('/customer/pets', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePet(id: string, data: Partial<Pet>): Promise<Pet> {
    return this.request<Pet>(`/customer/pets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePet(id: string): Promise<void> {
    return this.request<void>(`/customer/pets/${id}`, {
      method: 'DELETE',
    });
  }

  // Subscription endpoints
  async getSubscriptions(): Promise<Subscription[]> {
    return this.request<Subscription[]>('/customer/subscriptions');
  }

  async getSubscription(id: string): Promise<Subscription> {
    return this.request<Subscription>(`/customer/subscriptions/${id}`);
  }

  async pauseSubscription(id: string, until: string): Promise<Subscription> {
    return this.request<Subscription>('/customer/subscription/pause', {
      method: 'POST',
      body: JSON.stringify({ subscriptionId: id, pauseUntil: until }),
    });
  }

  async resumeSubscription(id: string): Promise<Subscription> {
    return this.request<Subscription>('/customer/subscription/resume', {
      method: 'POST',
      body: JSON.stringify({ subscriptionId: id }),
    });
  }

  async updateSubscriptionFrequency(
    id: string,
    frequency: Subscription['frequency']
  ): Promise<Subscription> {
    return this.request<Subscription>('/customer/subscription/frequency', {
      method: 'PUT',
      body: JSON.stringify({ subscriptionId: id, frequency }),
    });
  }

  async updateNextOrderDate(id: string, date: string): Promise<Subscription> {
    return this.request<Subscription>('/customer/subscription/next-order-date', {
      method: 'PUT',
      body: JSON.stringify({ subscriptionId: id, nextOrderDate: date }),
    });
  }

  async addSubscriptionItem(
    id: string,
    item: Omit<SubscriptionItem, 'id'>
  ): Promise<Subscription> {
    return this.request<Subscription>('/customer/subscription/add-item', {
      method: 'POST',
      body: JSON.stringify({ subscriptionId: id, item }),
    });
  }

  async removeSubscriptionItem(
    subscriptionId: string,
    itemId: string
  ): Promise<Subscription> {
    return this.request<Subscription>('/customer/subscription/remove-item', {
      method: 'POST',
      body: JSON.stringify({ subscriptionId, itemId }),
    });
  }

  async cancelSubscription(id: string, reason?: string): Promise<void> {
    return this.request<void>('/customer/subscription/cancel', {
      method: 'POST',
      body: JSON.stringify({ subscriptionId: id, reason }),
    });
  }

  // Order endpoints
  async getOrders(limit: number = 10): Promise<Order[]> {
    return this.request<Order[]>(`/customer/orders?limit=${limit}`);
  }

  async getOrder(id: string): Promise<Order> {
    return this.request<Order>(`/customer/orders/${id}`);
  }

  // Affiliate endpoints
  async getAffiliateStats(): Promise<AffiliateStats> {
    return this.request<AffiliateStats>('/customer/affiliate/stats');
  }

  // Prize draw endpoints
  async getPrizeDraws(): Promise<PrizeDraw[]> {
    return this.request<PrizeDraw[]>('/customer/prize-draws');
  }

  async getPrizeDrawEntries(drawId: string): Promise<number> {
    return this.request<number>(`/customer/prize-draws/${drawId}/entries`);
  }

  // Coupon endpoints
  async getCoupons(): Promise<Coupon[]> {
    return this.request<Coupon[]>('/customer/coupons');
  }

  async applyCoupon(code: string): Promise<{ valid: boolean; coupon?: Coupon }> {
    return this.request('/customer/coupons/validate', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  // Loyalty endpoints
  async getLoyaltyOverview(): Promise<LoyaltyOverview> {
    return this.request<LoyaltyOverview>(`/${this.token}/loyalty`);
  }

  async getTierProgress(): Promise<TierProgress> {
    return this.request<TierProgress>(`/${this.token}/tier-progress`);
  }

  async getBadges(): Promise<BadgesResponse> {
    return this.request<BadgesResponse>(`/${this.token}/badges`);
  }

  async getChallenges(): Promise<ChallengesResponse> {
    return this.request<ChallengesResponse>(`/${this.token}/challenges`);
  }

  async joinChallenge(challengeId: string): Promise<{ success: boolean; message: string; challengeName: string }> {
    return this.request(`/${this.token}/challenges/${challengeId}/join`, {
      method: 'POST',
    });
  }

  async convertPointsToCredit(points: number): Promise<ConvertPointsResult> {
    return this.request<ConvertPointsResult>(`/${this.token}/loyalty/convert-to-credit`, {
      method: 'POST',
      body: JSON.stringify({ points }),
    });
  }

  async getStoreCredit(): Promise<StoreCredit> {
    return this.request<StoreCredit>(`/${this.token}/store-credit`);
  }

  // Referral endpoints
  async getReferralSummary(): Promise<ReferralSummary> {
    return this.request<ReferralSummary>(`/${this.token}/referrals/summary`);
  }

  async enrollAsReferrer(): Promise<EnrollReferralResult> {
    return this.request<EnrollReferralResult>(`/${this.token}/referrals/enroll`, {
      method: 'POST',
    });
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
export default apiClient;
